/* eslint-disable no-useless-escape */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  useLicensors,
  useProducts,
  UseProductsResponse,
} from "@with-nx/ecommerce";
import { Formatter, useCMSClients, useMobile } from "@with-nx/hooks-n-helpers";
import {
  DebounceContainer,
  DesignedButton,
  DesignedInput,
  DesignedPagination,
} from "@with-nx/simple-ui/atoms";
import { LoadingContainer } from "@with-nx/simple-ui/molecules";
import {
  ReusableCheckboxSidebar,
  SearchEmpty,
} from "@with-nx/simple-ui/organisms";
import { Col, Row } from "antd";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { FiFilter } from "react-icons/fi";
import { Box } from "simple-effing-primitive-layout";

import ReusableGridProduct from "./ReusableGridProduct";

type GroupBy = "show" | "theme" | "global";
interface ReusableSearchSectionProps {
  loading?: boolean;
  productType?: unknown;
  groupBy?: GroupBy;
  hideFilter?: boolean;
  products?: UseProductsResponse;
  productCategory?:
    | "global"
    | "choreo-guides"
    | "scenic-projections"
    | "digital-drops";
  licensor?: number;
  allClientsMarked?: boolean;
  autoPopulateQuery?: boolean;
}

const ReusableSearchSection = ({
  loading = false,
  groupBy = "global",
  products: productsProp,
  productCategory,
  licensor,
  allClientsMarked,
  autoPopulateQuery,
}: ReusableSearchSectionProps) => {
  const [ready, setReady] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [selectedLicensors, setSelectedLicensors] = useState(
    licensor ? [licensor.toString()] : []
  );
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [filterQuery] = useState("");
  const [variation] = useState("");
  const [selectedResources, setSelectedResources] = useState([]);
  const [selectedShowBrandIds, setSelectedShowBrandIds] = useState<string[]>(
    []
  );
  const [selectedShowIds, setSelectedShowIds] = useState<string[]>([]);

  const router = useRouter();
  const _clients = useCMSClients({});
  const clients =
    _clients.clients?.filter((c) => {
      return c.showBrandTags?.length > 0 || c.showVariantTags?.length > 0;
    }) || [];
  const clientsLabels: {
    label: string;
    value: string;
  }[] = [];

  clients?.map((item) => {
    if (
      productCategory === "choreo-guides" &&
      item?.clientType !== "Choreographer"
    ) {
      return;
    }

    if (
      productCategory === "scenic-projections" &&
      item?.clientType !== "Designer"
    ) {
      return;
    }

    clientsLabels.push({
      label: item.title,
      value: item._id,
    });
  });

  const {
    products,
    isError: productsError,
    isLoading,
    search: triggerSearch,
    fetchParamsFromQuery,
  } = useProducts(
    {
      currentPage,
      pageSize: 24,
      searchText: searchText.trim(),
      filterQuery,
      selectedVersions,
      variation,
      productCategory,
      selectedResources,
      selectedLicensors: selectedLicensors,
      selectedShowIds: selectedShowIds,
      selectedShowBrandIds: selectedShowBrandIds,
      autoPopulateQuery: Boolean(autoPopulateQuery),
    },
    ready
  );

  useEffect(() => {
    if (!allClientsMarked || _clients.loading) {
      return;
    }

    const sb: string[] = [];
    const sv: string[] = [];
    _clients.clients.map((c) => {
      sb.push(...c.showBrandTags);
      sv.push(...c.showVariantTags);
    });

    setSelectedShowBrandIds(sb);
    setSelectedShowIds(sv);

    (async () => {
      await new Promise((resolve) => setTimeout(resolve, 100));
      triggerSearch("Clients Auo-Populate", {
        selectedShowBrandIds: sb,
        selectedShowIds: sv,
      });
    })();
  }, [_clients.loading]);

  const _products:
    | {
        page: 0;
        pageSize: 0;
        total: 0;
        result: [];
      }
    | undefined
    | null = products;
  const pagesAmount = !_products
    ? 0
    : isNaN(Math.ceil(_products?.total / _products?.pageSize))
    ? 1
    : Math.ceil(_products?.total / _products?.pageSize);

  useEffect(() => {
    if (searchText.length && !licensor) {
      setReady(true);
      return;
    }

    const currentSearchParams = new URLSearchParams(window.location.search);
    const s = currentSearchParams.get("s");

    if (s) {
      setSearchText(decodeURIComponent(s.toString()));
    }

    if (licensor) {
      setSelectedLicensors([licensor.toString()]);
    }

    setReady(true);
  }, []);

  useEffect(() => {
    const {
      searchText,
      selectedVersions,
      selectedResources,
      selectedLicensors,
      currentPage,
    } = fetchParamsFromQuery();

    if (searchText) {
      setSearchText(searchText);
    }

    if (selectedVersions?.length) {
      setSelectedVersions(selectedVersions);
    }

    if (selectedResources?.length) {
      setSelectedResources(selectedResources);
    }

    if (selectedLicensors?.length) {
      setSelectedLicensors(selectedLicensors);
    }

    if (currentPage) {
      setCurrentPage(currentPage);
    }
  }, []);

  const { licensors } = useLicensors();
  const licensorsLabels: {
    label: string;
    value: string | number;
    active?: boolean;
  }[] = [];

  licensors?.map((item) => {
    if (productCategory === "choreo-guides") {
      if (!["MTI", "TRW"].includes(item.abbreviation)) {
        return null;
      }
    }

    licensorsLabels.push({
      label: item.name,
      value: item.id,
      active:
        selectedLicensors.includes(String(item.id)) ||
        selectedLicensors.includes(String(item.id)),
    });
  });

  const mobile = useMobile();
  const [filter, _filter] = useState(false);

  if (productsError) return <div>Error...</div>;

  if (_clients.loading) {
    return <LoadingContainer />;
  }

  return (
    <>
      <div className="container" style={{ position: "relative", zIndex: 1 }}>
        <Row gutter={[36, 18]}>
          {licensor || mobile ? undefined : (
            <Col xs={24} sm={24} md={24} lg={8} xl={6}>
              {productCategory === "digital-drops" ||
              (licensorsLabels.length > 0 && !_clients.loading) ? (
                <ReusableCheckboxSidebar
                  groups={{
                    ...(licensor
                      ? {}
                      : {
                          licensors: {
                            label: "Licensors",
                            items: licensorsLabels,
                          },
                        }),
                    ...(productCategory === "global"
                      ? {
                          resources: {
                            label: "Resources",
                            items: [
                              {
                                label: "Scenic Projections",
                                value: "scenicProjections",
                                active:
                                  selectedResources.includes(
                                    "scenicProjections"
                                  ),
                              },
                              {
                                label: "Choreography Guides",
                                value: "choreoGuide",
                                active:
                                  selectedResources.includes("choreoGuide"),
                              },
                            ],
                          },
                        }
                      : {}),
                    versions: {
                      label: "Versions",
                      items: [
                        {
                          label: "Regular",
                          value: "regular",
                          active: selectedVersions.includes("regular"),
                        },
                        {
                          label: "Junior",
                          value: "junior",
                          active: selectedVersions.includes("junior"),
                        },
                      ],
                    },
                    ...(allClientsMarked
                      ? {}
                      : {
                          clients: {
                            label: "Clients",
                            items: clientsLabels,
                          },
                        }),
                  }}
                  change={(groups) => {
                    if (groups.licensors) {
                      setSelectedLicensors([
                        ...groups.licensors.items.map((item) =>
                          String(item.value)
                        ),
                      ]);
                    }
                    if (groups.clients) {
                      const sb: string[] = [];
                      const sv: string[] = [];
                      groups.clients.items.map((item) => {
                        const c = clients.find((c) => c._id === item.value);

                        if (c) {
                          sb.push(...c.showBrandTags);
                          sv.push(...c.showVariantTags);
                        }
                      });

                      setSelectedShowBrandIds(sb);
                      setSelectedShowIds(sv);
                    }
                    setSelectedVersions(
                      groups?.versions?.items.map((item) => item.value) || []
                    );
                    setSelectedResources(
                      groups?.resources?.items.map((item) => item.value) || []
                    );
                  }}
                />
              ) : undefined}
            </Col>
          )}
          <Col
            xs={24}
            sm={24}
            md={24}
            lg={licensor ? 24 : 16}
            xl={licensor ? 24 : 18}
          >
            <div id="results" />
            <Box css="d:flex" parse="mb:12">
              <Box parse={mobile ? "f:1 mr:12" : "f:1"}>
                <DebounceContainer
                  delay={1000}
                  value={searchText}
                  change={(searchText) => {
                    setCurrentPage(1);
                    setSearchText(
                      searchText && searchText?.length > 100
                        ? searchText.slice(0, 100)
                        : searchText
                    );
                  }}
                  render={({ value, set }) => (
                    <DesignedInput
                      bottom={0}
                      size="large"
                      icon="search/bold"
                      value={value}
                      change={(value) => set(value)}
                      placeholder="Search for your show"
                    />
                  )}
                />
              </Box>
              {mobile ? (
                <DesignedButton
                  icon={
                    <FiFilter
                      size={20}
                      strokeWidth={3}
                      color={filter ? "var(--primary)" : "var(--entity)"}
                      title="filter icon"
                    />
                  }
                  square={true}
                  size="large"
                  bypass={true}
                  theme="secondary"
                  press={() => _filter(!filter)}
                />
              ) : undefined}
            </Box>
            {mobile ? (
              <Box parse="mb:12" display={filter ? "block" : "none"}>
                {productCategory === "digital-drops" ||
                (licensorsLabels.length > 0 && !_clients.loading) ? (
                  <ReusableCheckboxSidebar
                    groups={{
                      ...(licensor
                        ? {}
                        : {
                            licensors: {
                              label: "Licensors",
                              items: licensorsLabels,
                            },
                          }),
                      ...(productCategory === "global"
                        ? {
                            resources: {
                              label: "Resources",
                              items: [
                                {
                                  label: "Scenic Projections",
                                  value: "scenicProjections",
                                },
                                {
                                  label: "Choreography Guides",
                                  value: "choreoGuide",
                                },
                              ],
                            },
                          }
                        : {}),
                      versions: {
                        label: "Versions",
                        items: [
                          {
                            label: "Regular",
                            value: "regular",
                          },
                          {
                            label: "Junior",
                            value: "junior",
                          },
                        ],
                      },
                      ...(allClientsMarked
                        ? {}
                        : {
                            clients: {
                              label: "Clients",
                              items: clientsLabels,
                            },
                          }),
                    }}
                    change={(groups) => {
                      if (groups.licensors) {
                        setSelectedLicensors([
                          ...groups.licensors.items.map((item) =>
                            String(item.value)
                          ),
                        ]);
                      }
                      if (groups.clients) {
                        const sb: string[] = [];
                        const sv: string[] = [];
                        groups.clients.items.map((item) => {
                          const c = clients.find((c) => c._id === item.value);

                          if (c) {
                            sb.push(...c.showBrandTags);
                            sv.push(...c.showVariantTags);
                          }
                        });

                        setSelectedShowBrandIds(sb);
                        setSelectedShowIds(sv);
                      }
                      setSelectedVersions(
                        groups?.versions?.items.map((item) => item.value) || []
                      );
                      setSelectedResources(
                        groups?.resources?.items.map((item) => item.value) || []
                      );
                    }}
                  />
                ) : undefined}
              </Box>
            ) : undefined}
            <Box display={!isLoading && pagesAmount <= 0 ? "block" : "none"}>
              <SearchEmpty />
            </Box>
            <ReusableGridProduct
              licensor={licensor}
              loading={isLoading}
              productCategory={productCategory}
              products={_products}
            />
            <div className="flex items-center justify-end">
              {!products || pagesAmount <= 0 ? undefined : (
                <DesignedPagination
                  mode="select"
                  size={pagesAmount}
                  value={currentPage - 1}
                  label={(index) => index + 1}
                  press={(index) => {
                    setCurrentPage(index + 1);
                    router.push("#results", "", {
                      scroll: true,
                    });
                  }}
                />
              )}
            </div>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default ReusableSearchSection;
